import React from 'react';
import FilterCommon, { useFilterCommon } from '@opm/components/FilterCommon';
import MarketContentBody from '@opm/components/MarketContentBody';
import { DBO_TM } from '@op/utils/format';
import { Justify, SearchBox, TabPanel, Tabs } from 'tea-component';
import CardCommon from '@op/components/CardCommon';
import SingleTranslateSelect from '@op/components/FilterBoxCommon/filters/languages/singleLauage';
import { ClassSpecial } from '@op/constants/common';
import SortBySelect from '@op/components/FilterBoxCommon/filters/single-select-factory';
import { cardIcon, tableIcon } from '@op/constants/chart';
import { LanguageType } from '@op/types/common';
import { DBOM_POINT } from '@opm/operationLog/buriedPoint';
import { VideoPoint } from '@opm/operationLog/pages';
import { useVideoTranslate } from '../../context';
import useIntls from '@opm/hooks/useIntls';
import ListView from './ListView';
import VideoTable from './VideoTable';
import { useXVideoData } from '../../hooks';
import { DEFAULT_METRICS } from '../../constants';
import { useScrollBottomRegister } from '@opm/components/ScrollBottomList';

interface IXTabProps {
  backCondConfig: any;
  translateCategory: (value: string, isEn: boolean) => string;
}

export default function XTab({
  backCondConfig,
  translateCategory,
}: IXTabProps) {
  // 页面进入埋点：tea Tabs destroyInactiveTabPanel=true，每次激活本 tab 都会重新触发
  DBOM_POINT.useInitPage({ buriedPoint: VideoPoint, pageExtInfo: 'twitter' });

  const { filterRef, filter, setFilter, loading, ...filterProps } =
    useFilterCommon({
      keys: [
        'MultiDateRange',
        'VideoXCategory',
        'VideoMediaTypes',
        'VideoCountryRegion',
        'VideoRelatedLanguages',
      ],
    });

  const { intl } = useIntls();
  const { language, setLanguage } = useVideoTranslate();

  const {
    nextPage,
    resetData,
    operation,
    sortBy,
    setSortBy,
    keyword,
    setKeyword,
    download,
    setOperation,
    changePageInfo,
    ...data
  } = useXVideoData({
    filter: filterRef.current,
    translateCategory,
  });

  const handleConfirm = (value: any) => {
    setFilter(value);
    resetData();
  };

  useScrollBottomRegister(nextPage);

  return (
    <MarketContentBody
      loading={loading}
      filterSlot={
        <FilterCommon
          {...filterProps}
          userDefaultValue={{
            ...DBO_TM.filterRangeDay(6, 0),
            date_type: 'daily',
          }}
          filterConfigProps={{
            VideoCountryRegion: {
              allValueConfig: 'global',
            },
          }}
          realTimeMode
          confirm={handleConfirm}
          backCondConfig={backCondConfig}
        />
      }
    >
      <CardCommon
        className="dbom-video-table-cardCommon"
        alwaysShowOperations
        title={intl('Trending Videos & Posts')}
        defaultOperation={operation}
        operations={[cardIcon, tableIcon]}
        hasDownload
        handleOperationsOnClick={(item: any) => {
          setOperation(item.symbol);
        }}
        handleDownloadOnClick={download}
      >
        <Justify
          left={
            <div className="dbom-video-table-filter">
              {operation === 'card' && (
                <SortBySelect
                  key="sortBy"
                  defaultValue={sortBy?.by}
                  onChange={(_key: any, value: any) => {
                    setSortBy({ order: 'desc', by: value });
                  }}
                  defaultParams={{
                    btnTextPrefix: intl({ id: 'Sort By' }),
                    options: [
                      { text: 'Date', value: 'publish_time' },
                      ...DEFAULT_METRICS.filter(
                        (item) => item.key !== 'retweet_count',
                      ).map((item) => ({
                        text: item.label,
                        value: item.key,
                      })),
                    ],
                  }}
                ></SortBySelect>
              )}
              <SingleTranslateSelect
                defaultValue={language}
                onChange={(key: string, value: LanguageType) =>
                  setLanguage(value)
                }
              ></SingleTranslateSelect>
            </div>
          }
          right={
            <SearchBox
              onSearch={(value = '') => setKeyword(value)}
              onClear={() => {
                setKeyword('');
              }}
              placeholder={intl({ id: 'Search video' })}
            ></SearchBox>
          }
        ></Justify>

        <Tabs activeId={operation} className={ClassSpecial.TAB_NONE}>
          <TabPanel id={'card'}>
            <ListView
              language={language}
              changePageInfo={changePageInfo}
              {...data}
            ></ListView>
          </TabPanel>
          <TabPanel id={'table'}>
            <VideoTable
              sortBy={sortBy}
              setSortBy={setSortBy}
              changePageInfo={changePageInfo}
              language={language}
              {...data}
            ></VideoTable>
          </TabPanel>
        </Tabs>
      </CardCommon>
    </MarketContentBody>
  );
}
